import { translateUi } from "./i18n.ts";
// Sinjalizimet e sigurise per Assessment (Faza A) - klon i thjeshtuar i src/lib/security-events.ts
// nga E:\Projekte ne programim\Assessments: grupe + dritare kohore per throttle/dedupe, qe nje
// nxenes qe del/hyn shpesh nga fokusi/fullscreen te mos e mbyste backend-in me evente identike.

export type SecurityEventType =
  | "fullscreen_exit" | "tab_hidden" | "window_blur" | "focus_lost"
  | "copy_blocked" | "paste_blocked" | "close_attempt" | "inactivity_detected"
  | "browser_automation_detected" | "synthetic_answer_blocked";

// Grupi qe cakton dritaren e throttle-it - disa lloje ndryshme (p.sh. tab_hidden/window_blur/
// fullscreen_exit) ndajne te njejtin grup sepse zakonisht ndodhin bashke (nxenesi del nga tab-i
// AND nga fullscreen njekohesisht) dhe s'ka kuptim te ruhen si evente te veçanta.
const EVENT_GROUPS: Record<SecurityEventType, string> = {
  fullscreen_exit: "focus_exit",
  tab_hidden: "focus_exit",
  window_blur: "focus_exit",
  focus_lost: "focus_exit",
  copy_blocked: "copy",
  paste_blocked: "paste",
  close_attempt: "close_page",
  inactivity_detected: "inactivity",
  browser_automation_detected: "browser_automation",
  synthetic_answer_blocked: "synthetic_answer",
};

const GROUP_THROTTLE_MS: Record<string, number> = {
  focus_exit: 60_000,
  copy: 5_000,
  paste: 5_000,
  close_page: 30_000,
  inactivity: 60_000,
  browser_automation: 60_000,
  synthetic_answer: 30_000,
};

export const SECURITY_EVENT_LABELS: Record<string, string> = {
  get copy() { return translateUi("Provoi të kopjonte tekst", "Attempted to copy text"); },
  get cut() { return translateUi("Provoi ta priste tekstin", "Attempted to cut text"); },
  get paste() { return translateUi("Provoi të ngjiste tekst", "Attempted to paste text"); },
  get fullscreen_exit() { return translateUi("Doli nga pamja në ekran të plotë", "Left fullscreen"); },
  get tab_hidden() { return translateUi("Ndërroi skedën e shfletuesit ose minimizoi dritaren", "Switched browser tab or minimised the window"); },
  get window_blur() { return translateUi("Dritarja e testit nuk ishte aktive", "The test window was not active"); },
  get focus_lost() { return translateUi("Dritarja e testit nuk ishte aktive", "The test window was not active"); },
  get copy_blocked() { return translateUi("Provoi të kopjonte tekst", "Attempted to copy text"); },
  get paste_blocked() { return translateUi("Provoi të ngjiste tekst", "Attempted to paste text"); },
  get close_attempt() { return translateUi("Provoi ta mbyllte ose ta rifreskonte faqen", "Attempted to close or refresh the page"); },
  get inactivity_detected() { return translateUi("Nuk pati aktivitet për 3 minuta", "No activity for 3 minutes"); },
  get browser_automation_detected() { return translateUi("Testi u hap në aplikacion AI ose shfletues të automatizuar", "Opened in an AI app or automated browser"); },
  get synthetic_answer_blocked() { return translateUi("U bllokua përgjigje e dhënë nga program, jo nga nxënësi", "Blocked an answer entered by a program, not the student"); },
  // Ngjarjet e kameres (CameraProctor) - shfaqen si etiketa te fotove-deshmi te "Vleresimi".
  get camera_notice_accepted() { return translateUi("Fillimi i testit (kamera u lejua)", "Test started (camera allowed)"); },
  get face_missing() { return translateUi("Fytyra nuk shihej në kamerë", "No face visible on camera"); },
  get multiple_faces() { return translateUi("Në kamerë u panë më shumë se një fytyrë", "More than one face detected on camera"); },
  get head_turned_left() { return translateUi("Koka ishte e kthyer anash", "Head turned away"); },
  get head_turned_right() { return translateUi("Koka ishte e kthyer anash", "Head turned away"); },
  get camera_denied() { return translateUi("Nuk u lejua përdorimi i kamerës", "Camera permission was not granted"); },
  get camera_unavailable() { return translateUi("Kamera nuk mund të përdorej", "The camera could not be used"); },
  get camera_stopped() { return translateUi("Kamera u ndal", "Camera stopped"); },
};

export function securityEventLabel(type: string): string {
  return SECURITY_EVENT_LABELS[type] ?? type.replace(/_/g, " ");
}

export function securityEventsExportNote(events: Array<{ eventType: string; details: string; createdAt: string }>): string {
  if (!events.length) return translateUi("Pa sinjalizime.", "No alerts.");
  return `${events.length}${translateUi(" sinjalizime:\n", " alerts:\n")}${events.map((event) =>
    `${event.createdAt} · ${securityEventLabel(event.eventType)}${event.details ? ` · ${event.details}` : ""}`,
  ).join("\n")}`;
}

export function groupQuestionSecurityEvents<T extends { questionId?: string | null }>(events: T[], questionIds: string[]) {
  const known = new Set(questionIds);
  const byQuestion = new Map<string, T[]>();
  const unassigned: T[] = [];
  for (const event of events) {
    if (!event.questionId || !known.has(event.questionId)) { unassigned.push(event); continue; }
    const list = byQuestion.get(event.questionId) ?? [];
    list.push(event); byQuestion.set(event.questionId, list);
  }
  return { byQuestion, unassigned };
}

// Mban kohen e fundit te secilit grup ne memorie (per jeten e komponentit) - jo e ruajtur ne
// localStorage/backend, thjesht per te evituar therrjet e perseritura brenda te njejtes sesion.
export function createSecurityEventThrottle() {
  const lastByGroup = new Map<string, number>();
  return function shouldRecord(type: SecurityEventType): boolean {
    const group = EVENT_GROUPS[type] ?? type;
    const throttleMs = GROUP_THROTTLE_MS[group] ?? 5_000;
    const now = Date.now();
    const last = lastByGroup.get(group) ?? 0;
    if (now - last < throttleMs) return false;
    lastByGroup.set(group, now);
    return true;
  };
}
